/* eslint-disable @repo/internal/react/require-jsdoc */
import React, { forwardRef, type ReactNode, type Ref } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, media, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

const rootStyles = xcss({
	display: 'block',
	position: 'relative',
	textDecoration: 'none',
	borderRadius: 'radius.xxlarge',
	width: '100%',
	overflow: 'hidden',
	':hover': {
		textDecoration: 'none',
	},
});

const appearanceStyleMap = {
	yellow: xcss({
		backgroundColor: 'color.background.accent.orange.subtlest',
		borderStyle: 'solid',
		// @ts-ignore Needs to be replaced with an outline instead of border. However, outline doesn't work on active
		borderColor: token('color.background.accent.orange.subtlest'),
		borderWidth: 'border.width',
		':hover': {
			borderStyle: 'solid',
			borderColor: 'color.border.bold',
			borderWidth: 'border.width',
		},
		':active': {
			borderStyle: 'solid',
			borderColor: 'color.border.accent.orange',
			borderWidth: 'border.width',
		},
		height: '100%',
	}),
	purple: xcss({
		backgroundColor: 'color.background.accent.purple.subtlest',
		borderStyle: 'solid',
		// @ts-ignore Needs to be replaced with an outline instead of border. However, outline doesn't work on active
		borderColor: token('color.background.accent.purple.subtlest'),
		borderWidth: 'border.width',
		':hover': {
			borderStyle: 'solid',
			borderColor: 'color.border.bold',
			borderWidth: 'border.width',
		},
		':active': {
			borderStyle: 'solid',
			borderColor: 'color.border.accent.purple',
			borderWidth: 'border.width',
		},
		height: '100%',
	}),
	blue: xcss({
		backgroundColor: 'color.background.accent.blue.subtlest',
		borderStyle: 'solid',
		// @ts-ignore Needs to be replaced with an outline instead of border. However, outline doesn't work on active
		borderColor: token('color.background.accent.blue.subtlest'),
		borderWidth: 'border.width',
		':hover': {
			borderStyle: 'solid',
			borderColor: 'color.border.bold',
			borderWidth: 'border.width',
		},
		':active': {
			borderStyle: 'solid',
			borderColor: 'color.border.accent.blue',
			borderWidth: 'border.width',
		},
		height: '100%',
	}),
	gray: xcss({ backgroundColor: 'color.background.neutral' }),
};

const borderStyleMap = {
	'color.border': xcss({
		borderStyle: 'solid',
		borderWidth: 'border.width',
		borderColor: 'color.border',
		':hover': {
			borderColor: 'color.border.bold',
		},
		':active': {
			borderColor: 'color.border.brand',
		},
	}),
};

const externalIconStyles = xcss({
	position: 'absolute',
	insetBlockStart: 'space.200',
	insetInlineEnd: 'space.200',
	backgroundColor: 'elevation.surface',
	borderRadius: token('radius.small'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '24px',
	height: '24px',
	[media.above.sm]: {
		width: 'unset',
		height: 'unset',
	},
});

type CardProps = {
	appearance?: 'yellow' | 'purple' | 'blue' | 'gray';
	borderColor?: 'color.border';
	externalIconSpacing?: 'spacious';
	href: string;
	children: ReactNode;
};

export const Card: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<CardProps> & React.RefAttributes<HTMLAnchorElement>
> = forwardRef(
	(
		{ appearance, borderColor, externalIconSpacing, href, children }: CardProps,
		ref: Ref<HTMLAnchorElement>,
	) => {
		return (
			<Anchor
				ref={ref}
				href={href}
				target={href.startsWith('http') ? '_blank' : undefined}
				xcss={[
					rootStyles,
					borderColor && borderStyleMap[borderColor],
					appearance && appearanceStyleMap[appearance],
				]}
			>
				{children}
				{href.startsWith('http') && (
					<Box xcss={externalIconStyles}>
						<LinkExternalIcon label="" color={token('color.icon')} spacing={externalIconSpacing} />
					</Box>
				)}
			</Anchor>
		);
	},
);
